import { PropsWithChildren, useState, FC } from 'react'
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form'
import { message, Modal, Spin } from 'antd'
import { JobRecordType } from '@ProdWb/JobList/types'
import { filterAttachmentIds } from '@ProdWb/logic'
import { RefetchExceptionsVariablesType } from '@ProdWb/components/ExceptionFailuresDrawer/types'

import {
    ExceptionCreator,
    ExceptionFailureInput,
    ExceptionFailureUpdateInput,
    MilestoneType,
    useExceptionFailureQuery,
} from '~/gql/graphql'
import Button from '~/core/Button'

import ExceptionForm from './ExceptionForm'
import {
    createException,
    MutationOperations,
    updateException,
    validateParts,
} from './logic'
import {
    NoteAttachmentType,
    PartsWithFailedQty,
    SelectedPartType,
} from './types'
import style from './styles.css'
import { EXCEPTION_FORM_FIELDS } from './constants'
interface ExceptionModalPropsType {
    exceptionId?: string
    handleCancel: () => void
    jobData?: JobRecordType
    jobName?: string | null
    refetchExceptionsVariables?: RefetchExceptionsVariablesType
}

export const ExceptionFailureModal: FC<
    PropsWithChildren<ExceptionModalPropsType>
> = props => {
    const {
        handleCancel,
        jobData,
        exceptionId,
        jobName,
        refetchExceptionsVariables,
    } = props

    const [loadingSubmitButton, setLoadingSubmitButton] = useState(false)
    const [restrictExceptionCreation, setRestrictExceptionCreation] =
        useState(false)
    const [restrictExceptionOnRFI, setRestrictExceptionOnRFI] = useState(false)
    const mutationHandler = MutationOperations()

    const { data: { exceptionFailure } = {}, loading } =
        useExceptionFailureQuery({
            variables: { id: exceptionId },
            fetchPolicy: 'network-only',
            skip: !exceptionId,
        })

    const confirmFileUpload = (param: string) => {
        if (param === 'inprogress') {
            setLoadingSubmitButton(true)
        } else {
            setLoadingSubmitButton(false)
        }
    }

    const handleExceptionSubmit = (
        form: WrappedFormUtils,
        noteAttachments: NoteAttachmentType[],
        exceptionAttachments: NoteAttachmentType[],
        partsWithQty: PartsWithFailedQty[],
    ) => {
        form.validateFields(async (error, values) => {
            if (error) {
                return
            }

            const selectedParts: SelectedPartType[] = []

            partsWithQty.forEach(part => {
                if (part.isChecked) {
                    selectedParts.push({
                        erpProductId: part.productId,
                        failedQuantity: part.failedQuantity,
                    })
                }
            })

            if (
                Object.prototype.hasOwnProperty.call(
                    values,
                    EXCEPTION_FORM_FIELDS.ALL_PARTS,
                )
            ) {
                if (selectedParts.length < 1) {
                    message.error('Part failed quantity not entered')
                    return
                } else {
                    const isPartsValidated = validateParts(partsWithQty)

                    if (!isPartsValidated) {
                        message.error(
                            'Failed quantity not entered or it is greater than ordered quantity',
                        )
                        return
                    }
                }
            }

            if (exceptionId) {
                setLoadingSubmitButton(true)
                const exceptionFailureUpdateInput = {
                    jobRequestId: values.selectedJobRequest || null,
                    type: values.selectedType,
                    milestoneType: values.milestoneType,
                    receiptId:
                        values.milestoneType === MilestoneType.inspection ||
                        values.milestoneType === MilestoneType.scheduling
                            ? null
                            : values.receiptId,
                    shipmentId: values.shipmentId || null,
                    qcId:
                        values.milestoneType === MilestoneType.inspection
                            ? values.receiptId
                            : null,
                    assignedEntity: values.assignedEntity,
                    failureDiscoveredBy: values.failureDiscoveredBy,
                    assignee: values.assignee,
                    actionId: values.action || null,
                    isFailure: values.isFailure || false,
                    isCustomerImpacted: values.wasCustomerAffected,
                    daysLate: values.daysLate || null,
                    assigneeId: values.fictivAssignee,
                    additionalContext: values.additionalContext || null,
                    exceptionAttachmentIds:
                        filterAttachmentIds(exceptionAttachments),
                } as ExceptionFailureUpdateInput

                const result = await updateException(
                    exceptionFailureUpdateInput,
                    values.note,
                    noteAttachments,
                    exceptionId,
                    selectedParts,
                    mutationHandler,
                )

                setLoadingSubmitButton(false)
                if (result?.exceptionId) {
                    handleCancel()
                    return
                }
                return
            }

            if (jobData) {
                setLoadingSubmitButton(true)

                const exceptionFailureInput = {
                    erpSaleOrderId: jobData.saleOrderId,
                    jobId: jobData.id,
                    type: values.selectedType,
                    creator: ExceptionCreator.fictiv,
                    jobRequestId: values.selectedJobRequest || null,
                    milestoneType: values.milestoneType,
                    receiptId:
                        values.milestoneType === MilestoneType.inspection
                            ? null
                            : values.receiptId,
                    shipmentId: values.shipmentId || null,
                    qcId:
                        values.milestoneType === MilestoneType.inspection
                            ? values.receiptId
                            : null,
                    assignedEntity: values.assignedEntity,
                    failureDiscoveredBy: values.failureDiscoveredBy,
                    assignee: values.assignee,
                    actionId: values.action || null,
                    isFailure: values.isFailure || false,
                    isCustomerImpacted: values.wasCustomerAffected,
                    daysLate: values.daysLate || null,
                    assigneeId: values.fictivAssignee,
                    additionalContext: values.additionalContext || null,
                    exceptionAttachmentIds:
                        filterAttachmentIds(exceptionAttachments),
                } as ExceptionFailureInput

                const result = await createException(
                    exceptionFailureInput,
                    selectedParts,
                    jobData.name,
                    values.note,
                    noteAttachments,
                    mutationHandler,
                    refetchExceptionsVariables,
                    jobData.isForJobBoard,
                )

                setLoadingSubmitButton(false)
                if (result?.exceptionId) {
                    handleCancel()
                    return
                }
                return
            }
        })
    }

    const disableCreateUpdateButton = () => {
        return restrictExceptionCreation || restrictExceptionOnRFI
    }

    return (
        <Modal
            title={
                !exceptionId
                    ? `Create exception - ${jobName || jobData?.name}`
                    : `Edit exception - ${jobName || jobData?.name}`
            }
            visible
            onCancel={event => {
                handleCancel()
                event.stopPropagation()
            }}
            centered
            maskClosable={false}
            width={750}
            bodyStyle={{
                padding: '0 16px',
                overflow: 'auto',
                height: '720px',
            }}
            footer={[
                <Button
                    key="cancel"
                    onClick={(event: { stopPropagation: () => void }) => {
                        event.stopPropagation()
                        handleCancel()
                    }}
                    placement="create-exception-modal"
                    name="cancel"
                    disabled={loadingSubmitButton}
                    data-test-target="cancel-create-exception"
                >
                    Cancel
                </Button>,
                <Button
                    type="primary"
                    form="exceptionForm"
                    key="createException"
                    htmlType="submit"
                    placement="create-exception-modal"
                    name="create-exception"
                    loading={loadingSubmitButton}
                    disabled={!!disableCreateUpdateButton()}
                    data-test-target="create-exception"
                    onClick={(event: { stopPropagation: () => void }) => {
                        event.stopPropagation()
                    }}
                >
                    {exceptionId ? 'Update exception' : 'Create exception'}
                </Button>,
            ]}
        >
            {(jobData || exceptionId) && (
                <Spin spinning={loading} className={style.formSpinStyle}>
                    <ExceptionForm
                        handleSubmit={handleExceptionSubmit}
                        jobData={jobData}
                        exceptionId={exceptionId}
                        confirmFileUpload={confirmFileUpload}
                        setRestrictExceptionCreation={
                            setRestrictExceptionCreation
                        }
                        setRestrictExceptionOnRFI={setRestrictExceptionOnRFI}
                        exception={exceptionFailure}
                        exceptionLoading={loading}
                    />
                </Spin>
            )}
        </Modal>
    )
}
